import React from 'react';
import { motion } from 'framer-motion';
import { SlideLayout } from '../SlideLayout';
import { TrendingDown, TrendingUp, Clock, RefreshCw, BarChart3 } from 'lucide-react';
const advantages = [{
  label: 'Deterministic sub-10ms latency',
  impact: "Enables $15–60/M SLA pricing commodity providers can't guarantee"
}, {
  label: 'Memory-bandwidth optimized',
  impact: '1.9× bandwidth per box vs single H100; efficient at low batch sizes where GPUs idle'
}, {
  label: 'Field-reprogrammable',
  impact: 'Adapt to Mamba, RWKV, MoE in weeks vs. 18–36 month ASIC cycles; no $50–200M NRE'
}, {
  label: '80% margins at 3,000 tok/sec',
  impact: 'vs. needing 52,000 tok/sec at commodity rates — a 17× easier bar'
}];
const timing = ['Inference spend overtaking training in 2026; real-time use cases growing 3× faster than batch', 'H100 spot collapse (65% price drop since 2024) makes commodity inference a race to zero', 'Enterprises pay 10–60× premiums for latency guarantees today — that spread is your wedge'];
export function ExecSummarySlide() {
  return <SlideLayout slideNumber={1} totalSlides={1} title="Executive Summary" data-id="element-23">
      <div className="w-full flex flex-col gap-5" data-id="element-24">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center" data-id="element-25">
          <div className="inline-block bg-brand-primary/10 border border-brand-primary/20 rounded-full px-4 py-1.5 text-brand-primary text-xs font-mono uppercase tracking-widest mb-3" data-id="element-26">
            Investor Brief
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight" data-id="element-27">
            The <span className="text-brand-primary" data-id="element-28">FPGA Arbitrage</span>
          </h2>
          <p className="text-base text-brand-muted mt-2 max-w-2xl mx-auto" data-id="element-29">
            AI inference is fragmenting into two markets. One is a race to zero.
            The other pays a premium for deterministic latency GPUs can't
            deliver.
          </p>
        </motion.div>

        {/* Two-tier market contrast */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="grid grid-cols-2 gap-4" data-id="element-30">
          <div className="bg-chip-gpu/5 border border-chip-gpu/20 rounded-2xl p-5" data-id="element-31">
            <div className="flex items-center gap-2 mb-3" data-id="element-32">
              <TrendingDown size={16} className="text-chip-gpu" data-id="element-33" />
              <span className="text-chip-gpu text-xs font-mono uppercase tracking-wider" data-id="element-34">
                Commodity Tier — Race to Zero
              </span>
            </div>
            <div className="text-3xl md:text-4xl font-mono font-bold text-chip-gpu mb-2" data-id="element-35">
              $0.17<span className="text-lg font-normal" data-id="element-36">/M tokens</span>
            </div>
            <p className="text-sm text-brand-muted leading-relaxed" data-id="element-37">
              Depreciated H100s flooding secondary markets at $1.49–2.10/hr.
              Needs{' '}
              <span className="text-white font-semibold" data-id="element-38">~17,000 tok/sec</span>{' '}
              to break even. Competing with distressed hardware.
            </p>
          </div>
          <div className="bg-brand-primary/5 border border-brand-primary/30 rounded-2xl p-5 relative overflow-hidden" data-id="element-39">
            <div className="absolute top-0 left-0 w-full h-0.5 bg-brand-primary" data-id="element-40" />
            <div className="flex items-center gap-2 mb-3" data-id="element-41">
              <TrendingUp size={16} className="text-brand-primary" data-id="element-42" />
              <span className="text-brand-primary text-xs font-mono uppercase tracking-wider" data-id="element-43">
                Premium Tier — Margin Machine
              </span>
            </div>
            <div className="text-3xl md:text-4xl font-mono font-bold text-brand-primary mb-2" data-id="element-44">
              $3–60<span className="text-lg font-normal" data-id="element-45">/M tokens</span>
            </div>
            <p className="text-sm text-brand-muted leading-relaxed" data-id="element-46">
              Voice agents, live coding, trading systems. Only{' '}
              <span className="text-white font-semibold" data-id="element-47">~3,000 tok/sec</span>{' '}
              gives 80% margin at $3/M. At $60/M:{' '}
              <span className="text-white font-semibold" data-id="element-48">750 tok/sec</span>.
            </p>
          </div>
        </motion.div>

        {/* Break-even visual */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} className="bg-brand-surface border border-brand-border rounded-2xl p-5" data-id="element-49">
          <div className="flex items-center gap-2 mb-4" data-id="element-50">
            <BarChart3 size={16} className="text-brand-muted" data-id="element-51" />
            <span className="text-brand-muted text-xs font-mono uppercase tracking-wider" data-id="element-52">
              $175K 8-FPGA Box — Throughput for 80% Gross Margin
            </span>
          </div>
          <div className="grid grid-cols-3 gap-4" data-id="element-53">
            {[{
            rate: '$0.17/M',
            label: 'Commodity',
            needed: '52,000',
            unit: 'tok/sec needed',
            verdict: 'Impossible',
            color: 'text-red-400',
            bg: 'bg-red-400/10',
            border: 'border-red-400/20'
          }, {
            rate: '$3/M',
            label: 'Interactive',
            needed: '2,965',
            unit: 'tok/sec needed',
            verdict: 'Achievable',
            color: 'text-brand-primary',
            bg: 'bg-brand-primary/10',
            border: 'border-brand-primary/20'
          }, {
            rate: '$60/M',
            label: 'Real-Time SLA',
            needed: '150',
            unit: 'tok/sec needed',
            verdict: 'Trivial',
            color: 'text-green-400',
            bg: 'bg-green-400/10',
            border: 'border-green-400/20'
          }].map(row => <div key={row.rate} className={`${row.bg} border ${row.border} rounded-xl p-4 text-center`} data-id="element-54">
                <div className={`text-xs font-mono ${row.color} mb-1`} data-id="element-55">
                  {row.rate} · {row.label}
                </div>
                <div className={`text-2xl md:text-3xl font-mono font-bold ${row.color}`} data-id="element-56">
                  {row.needed}
                </div>
                <div className="text-brand-muted text-sm" data-id="element-57">{row.unit}</div>
                <div className={`text-sm font-semibold mt-1 ${row.color}`} data-id="element-58">
                  {row.verdict}
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* FPGA advantages + market timing */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35
      }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-59">
          <div className="bg-brand-surface border border-brand-border rounded-2xl p-5" data-id="element-60">
            <div className="text-white text-xs font-mono uppercase tracking-wider mb-4" data-id="element-61">
              Why FPGAs Win the Premium Tier
            </div>
            <div className="space-y-3" data-id="element-62">
              {advantages.map((a, i) => <div key={i} className="flex gap-3" data-id="element-63">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-primary mt-2 shrink-0" data-id="element-64" />
                  <div data-id="element-65">
                    <span className="text-white text-sm font-semibold" data-id="element-66">
                      {a.label}:{' '}
                    </span>
                    <span className="text-brand-muted text-sm" data-id="element-67">{a.impact}</span>
                  </div>
                </div>)}
            </div>
          </div>

          <div className="bg-brand-surface border border-brand-border rounded-2xl p-5" data-id="element-68">
            <div className="flex items-center gap-2 mb-4" data-id="element-69">
              <Clock size={14} className="text-white" data-id="element-70" />
              <span className="text-white text-xs font-mono uppercase tracking-wider" data-id="element-71">
                Market Timing
              </span>
            </div>
            <div className="space-y-3" data-id="element-72">
              {timing.map((t, i) => <div key={i} className="flex gap-3" data-id="element-73">
                  <div className="w-1.5 h-1.5 rounded-full bg-yellow-400 mt-2 shrink-0" data-id="element-74" />
                  <p className="text-brand-muted text-sm leading-relaxed" data-id="element-75">
                    {t}
                  </p>
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Closing thesis */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="bg-brand-primary/5 border border-brand-primary/25 rounded-xl p-5" data-id="element-76">
          <div className="flex items-start gap-3" data-id="element-77">
            <RefreshCw size={18} className="text-brand-primary mt-0.5 shrink-0" data-id="element-78" />
            <p className="text-base text-white leading-relaxed" data-id="element-79">
              We're not building better GPUs. We're building the only hardware
              that makes premium-tier inference economics work at sub-hyperscale
              volume.{' '}
              <span className="text-brand-primary font-semibold" data-id="element-80">
                The customers who pay $15–60/M tokens can't get deterministic
                latency from GPU clouds — and that's a margin arbitrage, not a
                technology bet.
              </span>
            </p>
          </div>
        </motion.div>
      </div>
    </SlideLayout>;
}